import React, { useState } from 'react';

const RackOverviewTab = ({
  racks = [],
  loading = false,
  warehouses = [],
  onOpenAddItem,
  onSelectItem,
  onEditRack,
  onDeleteRack
}) => {
  const [expandedRacks, setExpandedRacks] = useState({});
  const [filterRackSearch, setFilterRackSearch] = useState('');

  const toggleRackExpand = (rackId) => {
    setExpandedRacks(prev => ({
      ...prev,
      [rackId]: !prev[rackId]
    }));
  };

  const filteredRacks = racks.filter(r => {
    if (!filterRackSearch.trim()) return true;
    const term = filterRackSearch.toLowerCase();
    return (
      r.rackNumber?.toLowerCase().includes(term) ||
      r.name?.toLowerCase().includes(term) ||
      r.zone?.toLowerCase().includes(term) ||
      r.aisle?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-4">
      {/* Top filter within Rack Overview */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
        <div className="relative flex-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Filter racks by rack number, name, zone or aisle..."
            value={filterRackSearch}
            onChange={(e) => setFilterRackSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all"
          />
          <svg
            className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-600 font-medium">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
          <span>Showing {filteredRacks.length} Racks</span>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm">Loading rack layouts...</p>
        </div>
      ) : filteredRacks.length === 0 ? (
        <div className="p-12 text-center bg-white border border-dashed border-gray-300 rounded-2xl shadow-sm">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7M4 7c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3M4 7h16M7 11h10M7 15h10" />
          </svg>
          <h3 className="text-sm font-bold text-gray-900">No Racks Found</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            {filterRackSearch ? 'No racks match your search criteria.' : 'No racks have been configured yet. Click "Create Rack" above to add your first storage rack.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRacks.map(rack => {
            const isExpanded = !!expandedRacks[rack._id];
            const storeObj = warehouses.find(w => w._id === (rack.store?._id || rack.store));
            const totalLevels = rack.levels?.length || 0;
            const totalPositions = (rack.levels || []).reduce((sum, l) => sum + (l.positions?.length || 0), 0);

            // Calculate stored items count across all positions
            let occupiedPositions = 0;
            let totalItemsStored = 0;
            (rack.levels || []).forEach(lvl => {
              (lvl.positions || []).forEach(pos => {
                const count = pos.allocations?.reduce((s, a) => s + (a.quantity || 0), 0) || 0;
                if (count > 0) occupiedPositions++;
                totalItemsStored += count;
              });
            });

            return (
              <div
                key={rack._id}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-indigo-300 transition-all overflow-hidden"
              >
                {/* Rack Header Banner */}
                <div
                  onClick={() => toggleRackExpand(rack._id)}
                  className="p-4 bg-gradient-to-r from-gray-50 to-white flex flex-col md:flex-row md:items-center justify-between gap-3 cursor-pointer hover:bg-indigo-50/20 transition-colors"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-black text-sm shadow-md flex-shrink-0">
                      {rack.rackNumber}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-extrabold text-gray-900">
                          Rack {rack.rackNumber}
                        </h3>
                        {rack.name && (
                          <span className="text-xs font-semibold text-gray-600">
                            • {rack.name}
                          </span>
                        )}
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-850">
                          {rack.status?.toUpperCase() || 'ACTIVE'}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>Store: <strong className="text-gray-700">{storeObj?.name || 'Warehouse'}</strong></span>
                        {rack.zone && <span>Zone: <strong className="text-gray-700">{rack.zone}</strong></span>}
                        {rack.aisle && <span>Aisle: <strong className="text-gray-700">{rack.aisle}</strong></span>}
                      </div>
                    </div>
                  </div>

                  {/* Right side stats & toggle */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 font-bold border border-indigo-100">
                        {totalLevels} Levels
                      </span>
                      <span className="px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 font-bold border border-purple-100">
                        {occupiedPositions}/{totalPositions} Positions Occupied
                      </span>
                      <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-bold border border-emerald-100">
                        {totalItemsStored} Units Stored
                      </span>
                    </div>

                    {/* Action buttons (Edit & Delete) */}
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => onEditRack && onEditRack(rack)}
                        className="p-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200"
                        title="Edit Rack Structure"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      <button
                        type="button"
                        onClick={() => onDeleteRack && onDeleteRack(rack)}
                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                        title="Delete Rack"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    <button
                      type="button"
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg
                        className={`w-4 h-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Expandable Level & Position Matrix */}
                {isExpanded && (
                  <div className="p-4 bg-gray-50/70 border-t border-gray-200 space-y-4">
                    {(rack.levels || []).map((level, lvlIdx) => (
                      <div
                        key={level._id || lvlIdx}
                        className="bg-white border border-gray-200 rounded-xl p-3.5 shadow-sm space-y-3"
                      >
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-md bg-purple-600 text-white font-extrabold text-xs flex items-center justify-center">
                              L{level.levelNumber}
                            </span>
                            <span className="text-xs font-bold text-gray-800">
                              Level {level.levelNumber} {level.name && `(${level.name})`}
                            </span>
                          </div>

                          <div className="text-xs text-gray-500">
                            {level.positions?.length || 0} Storage Positions
                          </div>
                        </div>

                        {/* Positions Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {(level.positions || []).map((pos, pIdx) => {
                            const allocations = pos.allocations || [];
                            const totalStored = allocations.reduce((s, a) => s + (a.quantity || 0), 0);
                            const hasItems = totalStored > 0;

                            return (
                              <div
                                key={pos._id || pIdx}
                                className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
                                  hasItems
                                    ? 'bg-emerald-50/40 border-emerald-300 shadow-sm'
                                    : 'bg-white border-dashed border-gray-200 hover:border-indigo-300'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-extrabold text-xs text-gray-900">
                                      {pos.positionNumber}
                                    </span>
                                    {pos.name && (
                                      <span className="text-[10px] text-gray-400">
                                        • {pos.name}
                                      </span>
                                    )}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenAddItem({
                                        store: rack.store?._id || rack.store,
                                        rackId: rack._id,
                                        levelId: level._id,
                                        positionId: pos._id
                                      });
                                    }}
                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded transition-colors"
                                  >
                                    + Add Item
                                  </button>
                                </div>

                                {/* Items allocated in this position */}
                                {hasItems ? (
                                  <div className="space-y-1.5 mt-1">
                                    {allocations.map((alloc, aIdx) => (
                                      <div
                                        key={alloc._id || aIdx}
                                        onClick={() => onSelectItem(alloc.item?._id || alloc.item)}
                                        className="bg-white border border-emerald-200 rounded-lg p-2 text-xs cursor-pointer hover:border-emerald-500 hover:shadow-sm transition-all"
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="font-bold text-gray-900 truncate flex-1">
                                            {alloc.item?.name || 'Item'}
                                          </span>
                                          <span className="font-black text-emerald-700 ml-1">
                                            {alloc.quantity} {alloc.unit || 'PCS'}
                                          </span>
                                        </div>
                                        <div className="text-[10px] text-gray-500 flex items-center justify-between mt-0.5">
                                          <span>SKU: {alloc.item?.sku || '—'}</span>
                                          <span className="text-indigo-600 font-semibold hover:underline">
                                            View Details →
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="py-3 text-center text-[11px] text-gray-400 font-medium">
                                    Empty Position
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RackOverviewTab;
